import * as React from "react";
import { Box, Button, Chip, IconButton, MenuItem, Stack, TextField, Typography } from "@mui/material";
import { PlusIcon } from "@phosphor-icons/react/dist/ssr/Plus";
import { XIcon } from "@phosphor-icons/react/dist/ssr/X";

import type { BriefMarket } from "@/hooks/api/use-campaign-briefs";
import type { RateCardItem } from "@/hooks/api/use-rate-card";
import { CustomFormHelperError } from "@/components/customFormHelper/custom-form-helper-error";

import { formatAmount } from "../../rate-card/format";
import { languageLabel, REGIONS } from "../constants";
import { amountOf, marketCode, PRICING } from "../estimate";
import { SectionCard } from "./section-card";

interface MarketsSectionProps {
	step: number;
	value: BriefMarket[];
	onChange: (markets: BriefMarket[]) => void;
	/** Undefined while the rate card loads; prices are left off until then. */
	card: RateCardItem[] | undefined;
	currency: string;
	error?: string;
}

/** Where the calls go and in which language, each market priced as it is added. */
export function MarketsSection({
	step,
	value,
	onChange,
	card,
	currency,
	error,
}: MarketsSectionProps): React.JSX.Element {
	const price = (code: string) => {
		const amount = card ? amountOf(card, code) : null;
		return amount === null ? null : formatAmount(currency, amount);
	};
	const english = price(PRICING.marketEnglish);
	const localised = price(PRICING.marketLocalised);
	const available = REGIONS.filter(({ value: region }) => !value.some((market) => market.region === region));

	return (
		<SectionCard
			id="markets"
			step={step}
			title="Markets and languages"
			description={
				english && localised
					? `Your first market comes with the campaign. Each further market is ${english} in English, or ${localised} in its own language.`
					: "Your first market comes with the campaign."
			}
		>
			<Box
				sx={{
					border: "1px solid",
					borderColor: error ? "error.main" : "var(--mui-palette-divider)",
					borderRadius: 1.5,
					overflow: "hidden",
				}}
			>
				{value.map((market, index) => {
					const region = REGIONS.find(({ value: code }) => code === market.region)!;
					const first = index === 0;
					const amount = first ? null : price(marketCode(market.language));
					return (
						<Stack
							key={market.region}
							direction="row"
							spacing={2}
							useFlexGap
							sx={{
								alignItems: "center",
								flexWrap: "wrap",
								px: 2,
								py: 1.5,
								borderBottom: "1px solid var(--mui-palette-divider)",
							}}
						>
							<Box
								aria-hidden
								sx={{
									flex: "none",
									width: 42,
									height: 42,
									borderRadius: 1.25,
									bgcolor: "text.primary",
									color: "background.paper",
									fontWeight: 700,
									fontSize: 14,
									letterSpacing: "0.04em",
									display: "inline-flex",
									alignItems: "center",
									justifyContent: "center",
								}}
							>
								{region.value}
							</Box>
							<Box sx={{ flex: "1 1 140px", minWidth: 0 }}>
								<Typography variant="subtitle2">{region.label}</Typography>
								<Typography variant="caption" color="text.secondary">
									{region.clock} local time
								</Typography>
							</Box>
							<TextField
								select
								size="small"
								label="Language"
								value={market.language}
								onChange={(event) =>
									onChange(
										value.map((other) =>
											other.region === market.region ? { ...other, language: event.target.value } : other
										)
									)
								}
								sx={{ minWidth: 190 }}
							>
								{region.languages.map((language) => (
									<MenuItem key={language} value={language}>
										{languageLabel(language)}
									</MenuItem>
								))}
							</TextField>
							<Box sx={{ flex: "none", minWidth: 120, textAlign: "right" }}>
								{first ? (
									<Chip label="Included" size="small" color="success" variant="outlined" />
								) : amount ? (
									<Chip label={`+${amount}`} size="small" color="primary" variant="outlined" />
								) : null}
							</Box>
							<IconButton
								aria-label={`Remove ${region.label}`}
								onClick={() => onChange(value.filter((other) => other.region !== market.region))}
							>
								<XIcon />
							</IconButton>
						</Stack>
					);
				})}
				{available.length > 0 ? (
					<Stack
						direction="row"
						spacing={1}
						useFlexGap
						sx={{
							alignItems: "center",
							flexWrap: "wrap",
							px: 2,
							py: 1.5,
							bgcolor: "var(--mui-palette-background-level1)",
						}}
					>
						<Typography variant="body2" color="text.secondary" sx={{ mr: 0.5 }}>
							{value.length === 0 ? "Add your first market" : "Add a market"}
						</Typography>
						{available.map((region) => (
							<Button
								key={region.value}
								size="small"
								variant="outlined"
								color="secondary"
								startIcon={<PlusIcon />}
								onClick={() => onChange([...value, { region: region.value, language: region.languages[0] }])}
								sx={{ borderRadius: 999 }}
							>
								{region.label}
							</Button>
						))}
					</Stack>
				) : null}
			</Box>
			{error ? <CustomFormHelperError text={error} /> : null}
		</SectionCard>
	);
}
